import React, { useRef, useState, useEffect, CSSProperties } from 'react';
import {
  KTSVG,
  SimpleAlert,
  maskPhoneNumber,
  cleanPhoneNumber,
} from '../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { StepperComponent } from '../../../../_mexablock/assets/ts/components';
import Stepper from '../../../../_mexablock/helpers/components/Stepper';
import { validatePhone, getAvailableZones } from '../../../service/operacion/CambioLada';
import AnimationSucces from '../../../../_mexablock/helpers/components/SuccesResponse';
import { changeLada } from '../../../service/operacion/CambioLada';
import Select from 'react-select';
import { statusSelectModalStyles } from '../../inventario/entrabas_salidas/models/statusSelectStyles';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  title: string;
  id_row: number;
};

const currentDate = new Date();
const fechaMaxima = new Date(currentDate);
fechaMaxima.setFullYear(currentDate.getFullYear() - 18);

const initValues = {
  id: 0,
  searchType: 'msisdn',
  iccid: null,
  msisdn: null,
  phone: '',
  estado_id: 0,
  lada_id: 0,
};
type ZoneMunicipio = {
  municipio: string;
  zona: number;
};

type ZoneEstado = {
  estado: string;
  municipios: ZoneMunicipio[];
};

type SelectOption = {
  label: string;
  value: string;
  zona?: number;
};

const stepperHeaders = [
  'Validacion',
  'Operación',
  'Confirmación',
  'Completada',
];

const radioButtonStyle = (isSelected: boolean): CSSProperties => ({
  WebkitAppearance: 'none',
  appearance: 'none',
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%',
  border: `2px solid ${isSelected ? 'orange' : '#ccc'}`,
  backgroundColor: isSelected ? 'orange' : 'transparent',
  display: 'inline-block',
  marginRight: '0.5rem',
  cursor: 'pointer',
  outline: 'none',
});

const ValidationSchema = [
  Yup.object().shape({
    msisdn: Yup.string()
      .nullable(true)
      .matches(/^\d+$/, 'El SIM debe ser un número')
      .test(
        'msisdn-required',
        'El número MSISDN es requerido',
        function (value) {
          const { iccid } = this.parent;
          // Si no hay ICCID, entonces MSISDN es requerido
          if (!iccid && !value) {
            return this.createError({
              message: 'El número MSISDN es requerido',
            });
          }
          return true;
        }
      ),
    iccid: Yup.string()
      .nullable(true)
      .matches(/^\d+$/, 'El ICCID debe ser un número')
      .test('iccid-required', 'El ICCID es requerido', function (value) {
        const { msisdn } = this.parent;
        // Si no hay MSISDN, entonces ICCID es requerido
        if (!msisdn && !value) {
          return this.createError({ message: 'El ICCID es requerido' });
        }
        return true;
      }),
  }),
  Yup.object().shape({
    estado_id: Yup.number()
      .required('El estado es requerido')
      .min(1, 'Seleccione un estado'),
    lada_id: Yup.number()
      .required('El municipio es requerido')
      .min(1, 'Seleccione un municipio'),
  }),
];

const CreateModal: React.FC<Props> = ({ show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const stepper = useRef<StepperComponent | null>(null);
  const [clientData, setClientData] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isCustomerVerified, setIsCustomerVerified] = useState(false);
  const [currentSchema, setCurrentSchema] = useState<any>(ValidationSchema[0]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [textStepper, setTextStepper] = useState('Continuar');
  const [zones, setZones] = useState<ZoneEstado[]>([]);
  const [estadoOptions, setEstadoOptions] = useState<SelectOption[]>([]);
  const [municipioOptions, setMunicipioOptions] = useState<SelectOption[]>([]);
  const [respuesta, setRespuesta] = useState<string>('');
  const [selectedEstado, setSelectedEstado] = useState<SelectOption | null>(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState<SelectOption | null>(null);
  const [selectedZona, setSelectedZona] = useState<number>(0);
  const [wasSuccessful, setWasSuccessful] = useState<boolean>(false);

  useEffect(() => {
    if (!show) return;

    (async () => {
      try {
        const res = await getAvailableZones();
        const list: ZoneEstado[] = Array.isArray(res) ? res : (res?.doc?.data ?? []);

        setZones(list);

        // estados
        const estados = list.map((e) => ({
          label: e.estado,
          value: e.estado,
        }));
        setEstadoOptions(estados);

        // limpia selección
        setSelectedEstado(null);
        setSelectedMunicipio(null);
        setSelectedZona(0);
        setMunicipioOptions([]);
      } catch (e) {
        SimpleAlert('Error al cargar zonas disponibles', 3000, 'error');
        setZones([]);
        setEstadoOptions([]);
        setMunicipioOptions([]);
      }
    })();
  }, [show]);

  const validateSimNumber = (simNumber: string) => {
    const simFormatRegex = /^\d{19}$/;

    return simFormatRegex.test(simNumber);
  };

  const formSim = useFormik({
    initialValues: initValues,
    validationSchema: currentSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setLoading(true);
      const curIndex = stepper.current?.currentStepIndex;
      switch (curIndex) {
        case 1:
          if (isCustomerVerified && clientData) {
            stepper.current?.goNext();
            setCurrentSchema(ValidationSchema[1]);
          } else {
            SimpleAlert('Por favor, valide los datos del cliente antes de continuar', 3000, 'warning');
          }
          setLoading(false);
          break;
        case 2:
          stepper.current?.goNext();
          setCurrentSchema(null);
          setLoading(false);
          break;
        case 3: {
          if (!selectedZona) {
            SimpleAlert('Debes seleccionar una zona', 3000, 'error');
            setLoading(false);
            return;
          }

          const payload: any = { zone: selectedZona };

          const msisdn = String(values.msisdn ?? '').trim();
          const iccid = String(values.iccid ?? '').trim();

          if (msisdn) {
            payload.msisdn = msisdn;
          } else if (iccid) {
            payload.iccid = iccid;
          } else {
            SimpleAlert('Debes capturar MSISDN o ICCID', 3000, 'error');
            setLoading(false);
            return;
          }

          changeLada(payload)
            .then((res) => {
              setRespuesta(res.data?.doc?.data?.new_msisdn);
              setWasSuccessful(true);
              stepper.current?.goNext();
            })
            .catch((err) => {
              SimpleAlert(
                err?.response?.data?.details?.[0]?.message ??
                err?.response?.data?.message ??
                'Ocurrió un error en la operación',
                3000,
                'error'
              );
            })
            .finally(() => {
              setLoading(false);
            });

          break;
        }
        case 4:

          onClose();

          break;
      }
    },
  });



  const handleMsisdn = (values: any) => {
    const value = values.target.value.toString();
    const maskedPhoneNumber = maskPhoneNumber(value);
    formSim.setFieldValue('phone', maskedPhoneNumber);
    formSim.setFieldValue('msisdn', cleanPhoneNumber(maskedPhoneNumber));
  };
  const handleIccidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const numericValue = value.replace(/\D/g, '');

    if (!validateSimNumber(numericValue)) {
      formSim.setFieldError('iccid', 'Formato de ICCID inválido');
    } else {
      formSim.setFieldError('iccid', '');
    }

    formSim.setFieldValue('iccid', numericValue);
  };

  const handleEstadoChange = (opt: SelectOption | null) => {
    setSelectedEstado(opt);
    setSelectedMunicipio(null);
    setSelectedZona(0);

    const estadoEncontrado = zones.find((z) => z.estado === opt?.value);

    const index = zones.findIndex(z => z.estado === opt?.value);
    formSim.setFieldValue('estado_id', opt ? index + 1 : 0);

    formSim.setFieldValue('lada_id', 0);

    if (!estadoEncontrado) {
      setMunicipioOptions([]);
      return;
    }

    const munOps: SelectOption[] = estadoEncontrado.municipios.map((m, idx) => ({
      label: m.municipio,
      value: m.municipio,
      zona: m.zona,
      id: idx + 1
    }));

    setMunicipioOptions(munOps);
  };

  const handleMunicipioChange = (opt: any | null) => {
    setSelectedMunicipio(opt);
    const zona = opt?.zona ?? 0;
    setSelectedZona(zona);

    formSim.setFieldValue('lada_id', opt ? opt.id : 0);
    formSim.setFieldValue('zona', zona);
  };

  const onClose = () => {
    handleClose(wasSuccessful);
    formSim.resetForm();
    setWasSuccessful(false);
    setTextStepper('Continuar');
    setClientData(null);
    setLoading(false);
    setCurrentSchema(ValidationSchema[0]);
    setSelectedOption(null);
    setSelectedEstado(null);
    setSelectedMunicipio(null);
    setSelectedZona(0);
    setMunicipioOptions([]);
  };

  const onValidateClient = async () => {
    setIsCustomerVerified(false);
    setClientData(null);

    const errors = await formSim.validateForm();
    formSim.setTouched({ msisdn: true, iccid: true } as any);

    if (
      (formSim.values.searchType === 'msisdn' && errors.msisdn) ||
      (formSim.values.searchType === 'iccid' && errors.iccid)
    ) {
      return;
    }

    const payload =
      formSim.values.searchType === 'msisdn'
        ? { msisdn: formSim.values.msisdn || formSim.values.phone, iccid: '' }
        : { msisdn: '', iccid: formSim.values.iccid };

    try {
      setIsValidating(true);

      const res = await validatePhone(payload);

      const data = res?.data?.doc?.data;

      if (data) {
        setClientData(data);
        setIsCustomerVerified(true);

        formSim.setFieldValue('customer_id', data.id);
        formSim.setFieldValue('validated_phone', data.phone);
        formSim.setFieldValue('validated_iccid', data.iccid);
      }
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message, 3000, 'error');
      setIsCustomerVerified(false);
      setClientData(null);


    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    setIsCustomerVerified(false);
    setClientData(null);
  }, [formSim.values.searchType, formSim.values.phone, formSim.values.iccid]);

  return (
    <>
      <Modal
        id="kt_modal_create"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered modal-xl"
        show={show}
        onHide={onClose}
      >
        <div className="modal-content">
          <div className="modal-header pb-0 border-0 justify-content-end">
            <div
              className="btn btn-sm btn-icon btn-active-color-primary"
              data-bs-dismiss="modal"
              onClick={onClose}
            >
              <KTSVG
                className="svg-icon-2"
                path="/media/icons/duotune/arrows/arr061.svg"
              />
            </div>
          </div>

          <div className="modal-body scroll-y mx-5 pt-0">
            <div className="text-center mb-13">
              <h1 className="mb-3">Cambio de LADA</h1>
              <div className="text-muted fw-semibold fs-5">
                Selecciona y agrega el dato correspondiente.
              </div>
            </div>
            <form
              id="kt_modal_generic_form"
              className="form"
              action="#"
              onSubmit={formSim.handleSubmit}
            >
              <Stepper
                headers={stepperHeaders}
                loading={loading}
                onCancel={onClose}
                stepper={stepper}
                textButton={textStepper}
                onBack={(actionBack: any) => {
                  if (actionBack == 1) {
                    setCurrentSchema(null);
                  }
                  if (actionBack == 0) {
                    setCurrentSchema(ValidationSchema[0]);
                  }
                }}
              >
                <div className="w-100 container">
                  <div className="mb-6">
                    <h5 className="fw-bold text-gray-700 mb-2">Validación de cliente</h5>
                    <div className="separator border-1 my-0"></div>
                  </div>
                  {/* Buscar por */}
                  <div className="row align-items-center mb-10">
                    {/* Radios + label */}
                    <div className="col-md-4 d-flex flex-column">
                      <label className="fw-bold fs-4 mb-6">Buscar por:</label>

                      {/* Wrapper para que no se encimen */}
                      <div className="d-flex align-items-center gap-10">
                        <label className="form-check form-check-custom form-check-solid d-flex align-items-center gap-3 m-0">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="searchType"
                            value="msisdn"
                            checked={formSim.values.searchType === 'msisdn'}
                            onChange={() => {
                              formSim.setFieldValue('searchType', 'msisdn');
                              formSim.setFieldValue('phone', '');
                              formSim.setFieldValue('iccid', '');
                              formSim.setFieldValue('msisdn', '');
                            }}
                            style={radioButtonStyle(formSim.values.searchType === 'msisdn')}
                          />
                          <span className="fw-semibold fs-5">MSISDN</span>
                        </label>

                        <label className="form-check form-check-custom form-check-solid d-flex align-items-center gap-3 m-0">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="searchType"
                            value="iccid"
                            checked={formSim.values.searchType === 'iccid'}
                            onChange={() => {
                              formSim.setFieldValue('searchType', 'iccid');
                              formSim.setFieldValue('phone', '');
                              formSim.setFieldValue('iccid', '');
                              formSim.setFieldValue('msisdn', '');
                            }}
                            style={radioButtonStyle(formSim.values.searchType === 'iccid')}
                          />
                          <span className="fw-semibold fs-5">ICCID</span>
                        </label>
                      </div>
                    </div>

                    {/* Input */}
                    <div className="col-md-6">
                      {formSim.values.searchType === 'msisdn' && (
                        <>
                          {formSim.touched.msisdn && formSim.errors.msisdn && (
                            <div className="fv-plugins-message-container text-danger mb-2">
                              <span role="alert">{formSim.errors.msisdn}</span>
                            </div>
                          )}
                          <input
                            className="form-control form-control-solid py-4"
                            value={formSim.values.phone}
                            placeholder="Ingresa número MSISDN"
                            onChange={handleMsisdn}
                          />
                        </>
                      )}

                      {formSim.values.searchType === 'iccid' && (
                        <>
                          {formSim.touched.iccid && formSim.errors.iccid && (
                            <div className="fv-plugins-message-container text-danger mb-2">
                              <span role="alert">{formSim.errors.iccid}</span>
                            </div>
                          )}
                          <input
                            className="form-control form-control-solid py-4"
                            value={formSim.values.iccid || ''}
                            placeholder="Ingresa número ICCID"
                            onChange={handleIccidChange}
                            maxLength={19}
                          />
                        </>
                      )}
                    </div>

                    {/* Botón */}
                    <div className="col-md-2 d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-warning fw-bold px-10 py-4"
                        onClick={onValidateClient}
                        disabled={isValidating}
                        style={{ backgroundColor: 'orange' }}
                      >
                        {isValidating ? 'Validando...' : 'Validar'}
                      </button>
                    </div>
                  </div>

                  {clientData && (
                    <>
                      <div className="mb-6">
                        <h6 className="fw-bold text-gray-500 mb-2">Datos del cliente</h6>
                        <div className="separator border-1 my-0"></div>
                      </div>
                      <div className="row g-6 mb-6">
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Nombre</label>
                          <input className="form-control bg-light" value={clientData?.first_name ?? ''} disabled />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Apellido Paterno</label>
                          <input className="form-control bg-light" value={clientData?.father_last_name ?? ''} disabled />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Apellido materno</label>
                          <input className="form-control bg-light" value={clientData?.mother_last_name ?? ''} disabled />
                        </div>
                      </div>
                      <div className="row g-6">
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Fecha de activación</label>
                          <div className="position-relative">
                            <input
                              className="form-control bg-light"
                              value={clientData?.activation_date ? new Date(clientData.activation_date).toLocaleDateString('es-MX') : ''}
                              disabled
                            />
                            <span className="position-absolute top-50 end-0 translate-middle-y me-4 text-gray-500">
                              <i className="bi bi-calendar3 fs-4"></i>
                            </span>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">Número actual</label>
                          <input className="form-control bg-light" value={clientData?.phone ?? ''} disabled />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">ICCID</label>
                          <input className="form-control bg-light" value={clientData?.iccid ?? ''} disabled />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* Cambio de lada */}
                <div className="w-100 container mt-20">
                  {/* Título y separador */}
                  <div className="mb-6">
                    <h3 className="fw-bold text-gray-500 mb-2">Nueva LADA</h3>
                    <div className="separator border-1 my-0"></div>
                  </div>

                  {/* Fila de Selectores */}
                  <div className="row">
                    <div className="col-6 mb-4">
                      <label className="fw-bold fs-6 mb-2 required">Estado</label>
                      <Select
                        options={estadoOptions}
                        value={selectedEstado}
                        onChange={handleEstadoChange}
                        placeholder="Seleccione"
                        noOptionsMessage={() => <span>No hay registros</span>}
                        menuPortalTarget={document.body}
                        styles={statusSelectModalStyles}
                      />
                    </div>

                    <div className="col-6 mb-4">
                      <label className="fw-bold fs-6 mb-2 required">Municipio</label>
                      <Select
                        options={municipioOptions}
                        value={selectedMunicipio}
                        onChange={handleMunicipioChange}
                        placeholder="Seleccione"
                        isDisabled={!selectedEstado}
                        noOptionsMessage={() => <span>No hay registros</span>}
                        menuPortalTarget={document.body}
                        styles={statusSelectModalStyles}
                      />
                    </div>
                  </div>

                  {/* Texto descriptivo inferior */}
                  <div className="text-muted fw-semibold fs-6">
                    Seleccione el estado y municipio para asignar la nueva LADA que tendra el cliente.
                  </div>
                </div>
                {/* Fin Cambio de Lada */}
                <div className="w-100 container mt-10">
                  {/* Encabezado de la confirmación */}
                  <div className="text-center mb-10">
                    <h2 className="fw-bolder text-gray-700 mb-3">
                      Confirmar Cambio de Numeración (LADA)
                    </h2>
                    <div className="text-muted fw-semibold fs-5">
                      Estás por cambiar la LADA del usuario. Antes de continuar, verifica lo siguiente:
                    </div>
                  </div>

                  {/* Tarjeta gris con los datos */}
                  <div className="bg-light rounded p-8 mb-8">
                    <div className="row g-5">
                      {/* Columna Titular */}
                      <div className="col-md-4 d-flex flex-column">
                        <span className="text-muted fw-semibold fs-7 mb-1">Titular:</span>
                        <span className="fw-bold text-gray-800 fs-5">
                          {clientData
                            ? `${clientData.first_name || ''} ${clientData.father_last_name || ''} ${clientData.mother_last_name || ''}`.trim()
                            : 'N/A'}
                        </span>
                      </div>

                      {/* Columna MSISDN a cambiar */}
                      <div className="col-md-4 d-flex flex-column">
                        <span className="text-muted fw-semibold fs-7 mb-1">MSISDN a cambiar:</span>
                        <span className="fw-bold text-gray-800 fs-5">
                          {clientData?.phone || 'N/A'}
                        </span>
                      </div>

                      {/* Columna Ubicación de nueva LADA */}
                      <div className="col-md-4 d-flex flex-column">
                        <span className="text-muted fw-semibold fs-7 mb-1">Ubi. de nueva LADA:</span>
                        <span className="fw-bold text-gray-800 fs-5">
                          {selectedMunicipio && selectedEstado
                            ? `${selectedMunicipio.label}, ${selectedEstado.label}`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Alerta de advertencia amarilla */}
                  <div className="d-flex align-items-center bg-light-warning rounded p-5 mb-10">
                    {/* Icono de advertencia */}
                    <span className="svg-icon svg-icon-warning svg-icon-2hx me-4">
                      <i className="bi bi-exclamation-triangle-fill text-warning fs-1"></i>
                    </span>

                    {/* Texto de advertencia */}
                    <div className="d-flex flex-column">
                      <span className="text-gray-700 fw-semibold fs-6">
                        Esta acción es irreversible, por lo que una vez realizado el cambio, el cliente no podrá recuperar su número anterior.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column justify-content-center align-items-center w-100">
                  <h1 className="text-primary fw-bolder text-center">
                    Operación completada exitosamente
                  </h1>
                  <div className="text-center mt-5 fs-3 fw-bold text-gray-500">
                    El nuevo MSISDN es: <span className="fw-bolder text-gray-600">{respuesta}</span>
                  </div>
                  <div className="w-350px rounded mx-auto d-block">
                    <AnimationSucces />
                  </div>
                </div>
              </Stepper>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};
export { CreateModal };
